import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Button from '@/components/ui/Button';
import { putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { ManagedUserDtoIn } from '@/models/in/ManagedUserDtoIn';
import { ChangeUserEmailDtoOut, ManagedUserDetailsDtoOut } from '@/models/out/ManagedUserAccessDtoOut';
import CompactDialog from './CompactDialog';

const UPDATE_DETAILS_URL = 'users/update-details';
const CHANGE_EMAIL_URL = 'users/change-email';

/** Same shape the server accepts; it checks again with MailAddress. */
const looksLikeEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** Loose check only - the server normalizes with libphonenumber and accepts non-Israeli numbers too. */
const hasEnoughDigits = (phone: string) => phone.replace(/\D/g, '').length >= 9;

interface EditUserDetailsDialogProps {
  open: boolean;
  user: ManagedUserDtoIn;
  /** The admin's own account: its email can't be changed here, the session is bound to it. */
  isSelf: boolean;
  onClose: () => void;
  /** Gets the user as saved - with the new email when it changed. */
  onSuccess: (updated: ManagedUserDtoIn) => void;
}

const EditUserDetailsDialog = ({ open, user, isSelf, onClose, onSuccess }: EditUserDetailsDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();

  const [email, setEmail] = useState('');
  // The address the account has right now - it moves on a successful change even when saving the
  // other details then fails, so a second "save" doesn't try to move it again.
  const [accountEmail, setAccountEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(user.email ?? '');
      setAccountEmail(user.email ?? '');
      setFirstName(user.firstName ?? '');
      setLastName(user.lastName ?? '');
      setPhoneNumber(user.phoneNumber ?? '');
      setError(null);
    }
  }, [open, user]);

  const newEmail = email.trim();
  const emailChanged = !isSelf && newEmail.toLowerCase() !== accountEmail.toLowerCase();

  const handleSave = async () => {
    if (emailChanged && !looksLikeEmail(newEmail)) {
      setError(t('usersManagement.editDetails.invalidEmail'));
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('usersManagement.editDetails.nameRequired'));
      return;
    }
    if (!hasEnoughDigits(phoneNumber)) {
      setError(t('usersManagement.editDetails.invalidPhone'));
      return;
    }

    setError(null);
    setIsSubmitting(true);
    const headers = { Authorization: `Bearer ${currentUser?.token}` };

    // The email first: it is the one that can be refused (taken, malformed), and nothing has
    // changed yet when it is. The details are then saved under the address the account now has.
    if (emailChanged) {
      try {
        await putAuth<void, object, ChangeUserEmailDtoOut>(CHANGE_EMAIL_URL, headers, {}, { currentEmail: accountEmail, newEmail });
      } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        setError(t(status === 409 ? 'usersManagement.editDetails.emailTaken' : 'usersManagement.editDetails.emailChangeError'));
        setIsSubmitting(false);
        return;
      }
      setAccountEmail(newEmail);
    }

    const savedEmail = emailChanged ? newEmail : accountEmail;
    const body: ManagedUserDetailsDtoOut = {
      email: savedEmail,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneNumber: phoneNumber.trim(),
    };

    try {
      await putAuth<void, object, ManagedUserDetailsDtoOut>(UPDATE_DETAILS_URL, headers, {}, body);
      onSuccess({ ...user, ...body });
      onClose();
    } catch {
      if (emailChanged) {
        // The email did move - the list must show it even though the rest wasn't saved.
        onSuccess({ ...user, email: savedEmail });
      }
      setError(t('usersManagement.editDetails.saveError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const field = (id: string, label: string, value: string, onChange: (value: string) => void, dir?: 'ltr') => (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="body-sm-semibold text-(--font-secondary)">
        {label}
      </label>
      <input
        id={id}
        type="text"
        dir={dir}
        className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (error) setError(null);
        }}
      />
    </div>
  );

  return (
    <CompactDialog
      open={open}
      onClose={onClose}
      title={t('usersManagement.editDetails.title')}
      footer={
        <>
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      {isSelf ? (
        <div className="flex flex-col gap-1">
          <span className="body-sm-semibold text-(--font-secondary)">{t('email')}</span>
          <span dir="ltr" className="body-sm-regular text-(--font-body-2) text-start">
            {user.email}
          </span>
          <span className="body-xs-regular text-(--font-secondary)">{t('usersManagement.editDetails.selfEmailHint')}</span>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {field('user-email', t('email'), email, setEmail, 'ltr')}
          {emailChanged && <span className="body-xs-regular text-(--font-secondary)">{t('usersManagement.editDetails.emailChangeHint')}</span>}
        </div>
      )}

      {field('user-first-name', t('firstName'), firstName, setFirstName)}
      {field('user-last-name', t('lastName'), lastName, setLastName)}
      {field('user-phone', t('phoneNumber'), phoneNumber, setPhoneNumber, 'ltr')}

      {error && <p className="text-red-500 body-sm-regular">{error}</p>}
    </CompactDialog>
  );
};

export default EditUserDetailsDialog;
